import { create } from 'zustand';

interface Event {
  timestamp: number;
  data?: any;
}

type EventListener = (data?: any) => void;

interface EventState {
  events: { [key: string]: Event };
  listeners: { [key: string]: EventListener[] };
  triggerEvent: (eventName: string, data?: any) => void;
  listenToEvent: (eventName: string, callback: EventListener) => void;
  cleanupEvents: () => void;
}

const MAX_EVENT_AGE = 5 * 60 * 1000; // 5 minutes

const useEventStore = create<EventState>((set, get) => ({
  events: {},
  listeners: {},

  triggerEvent: (eventName, data) => {
    const currentTime = Date.now();

    // Clean up old events
    get().cleanupEvents();

    // Trigger the new event
    set((state) => {
      // Invoke all listeners for this event
      const listeners = state.listeners[eventName] || [];
      listeners.forEach((listener) => listener(data));

      return {
        events: {
          ...state.events,
          [eventName]: { timestamp: currentTime, data },
        },
      };
    });
  },

  listenToEvent: (eventName, callback) => {
    set((state) => ({
      listeners: {
        ...state.listeners,
        [eventName]: [...(state.listeners[eventName] || []), callback],
      },
    }));
  },

  cleanupEvents: () => {
    const currentTime = Date.now();
    set((state) => {
      const updatedEvents = Object.fromEntries(
        Object.entries(state.events).filter(
          ([, event]) => currentTime - event.timestamp < MAX_EVENT_AGE
        )
      );
      return { events: updatedEvents };
    });
  },
}));

// Export store selectors instead of direct method references
export const triggerEvent = (eventName: string, data?: any) => useEventStore.getState().triggerEvent(eventName, data);
export const listenToEvent = (eventName: string, callback: EventListener) => useEventStore.getState().listenToEvent(eventName, callback);

export default useEventStore;
